import { useEffect } from "react";

// Keeps a page inside its socket room for as long as it is mounted.
//
// Socket.io reconnects with a brand new socket that belongs to no rooms, so
// joining once on mount is not enough: after any drop, a laptop sleeping or the
// wifi blinking, the page keeps its old listeners but never hears anything
// again until it is reloaded. That is what "it only updates if I refresh" looks
// like. Re-joining on every `connect` fixes it, and the cleanup stops a remount
// from stacking another copy of the handler.
//
// The server registers its join handlers up front, so the join can be sent
// straight away rather than waiting for an entry acknowledgement.
const ENTRY = {
  bidder: "bidder-entry",
  curator: "curator-entry",
  public: "public-entry",
};

const JOIN = {
  bidder: "join-bidder-session",
  curator: "join-curator-session",
  public: "join-session",
};

export function useSocketRoom(socket, { role, id, sessionId }) {
  useEffect(() => {
    const entry = ENTRY[role];
    const join = JOIN[role];

    // A public viewer has no id of their own; everyone else needs both.
    const ready =
      socket && entry && sessionId && (role === "public" || Boolean(id));
    if (!ready) return undefined;

    const enter = () => {
      if (role === "public") socket.emit(entry);
      else socket.emit(entry, id);
      socket.emit(join, sessionId);
    };

    enter();
    socket.on("connect", enter);

    return () => {
      socket.off("connect", enter);
    };
  }, [socket, role, id, sessionId]);
}

export default useSocketRoom;
